import React, { useEffect, useState } from "react";
import {
    PrimaryButton, DetailsListLayoutMode, IColumn,
    SpinButton, Position,
    Dropdown, IDropdownOption,
    ShimmeredDetailsList,
    ScrollablePane,
    ConstrainMode,
    TextField,
    Stack,
    Dialog,
    DialogType,
    DialogFooter,
    DefaultButton,
} from "@fluentui/react";
import {
    queryWorkflowTemplates,
    queryWorkflowList,
    postProcessFile,
    reverseSlidemasterFile,
    generateAltTextImages,
    deleteWorkflow,
    compressTemplate,
    generateOneSlide,
    changeImage,
    changeThemeColor,
} from "src/services/aigc";

import { useNavigate } from "react-router-dom";
import { OnlineWorkflowTemplates, OnlineWorkflow, TopicInput } from "src/types/workflows";
import { FeedbackDialog } from "src/components/FeedbackDialog";
import { columns as initialColumns } from "./constants";
import style from "./index.module.scss";

const pageSizeOptions = [
    {
        text: "20",
        key: 20,
    },
    {
        text: "30",
        key: 30,
    },
    {
        text: "50",
        key: 50,
    },
    {
        text: "",
        key: "",
    },
];

const handleDelete = async (workflowId: string) => {
    if (window.confirm(`Are you sure you want to delete workflow ${workflowId}?`)) {
        try {
            const response = await deleteWorkflow({ workflowId });
            console.log("Success:", response);
            window.location.reload();
        } catch (error) {
            console.error("Error:", error);
        }
    }
};

export const Workflow = () => {
    const [file, setFile] = useState<File | null>(null);
    const [fileUrl, setFileUrl] = useState<string | null>(null);
    const [fileName, setFileName] = useState<string | null>(null);
    const [reverseFile, setReverseFile] = useState<File | null>(null);
    const [reverseFileName, setReverseFileName] = useState<string | null>(null);
    const [reverseFileUrl, setReverseFileUrl] = useState<string | null>(null);

    const [imageFiles, setImageFiles] = useState<FileList>();
    const [altTextUrls, setAltTextUrls] = useState<string[] | null>(null);
    const [imagePreviewUrls, setImagePreviewUrls] = useState<string[]>([]);

    const [imageQuality, setImageQuality] = useState<number>(90);
    const [compressFile, setCompressFile] = useState<File | null>(null);
    const [compressFileUrl, setCompressFileUrl] = useState<string | null>(null);
    const [compressFileName, setCompressFileName] = useState<string | null>(null);

    const [slideImage, setSlideImage] = useState<File | null>(null);
    const [slideImageName, setSlideImageName] = useState<string | null>(null);
    const [slideFileUrl, setSlideFileUrl] = useState<string | null>(null);

    const [templateFile, setTemplateFile] = useState<File | null>(null);
    const [templateFileName, setTemplateFileName] = useState<string | null>(null);
    const [changeImageFile, setChangeImageFile] = useState<File | null>(null);
    const [changedFileUrl, setChangedFileUrl] = useState<string | null>(null);

    const [themeColorFile, setThemeColorFile] = useState<File | null>(null);
    const [themeColorFileName, setThemeColorFileName] = useState<string | null>(null);
    const [themeColorFileUrl, setThemeColorFileUrl] = useState<string | null>(null);
    const [isColorPickerDialogOpen, setIsColorPickerDialogOpen] = useState(false);
    const [themeColors, setThemeColors] = useState<{ [key: string]: string }>({
        dark1: "#000000",
        light1: "#FFFFFF",
        dark2: "#2F2F2F",
        light2: "#D3D3D3",
        accent1: "#5B9BD5",
        accent2: "#ED7D31",
        accent3: "#A5A5A5",
        accent4: "#FFC000",
        accent5: "#4472C4",
        accent6: "#70AD47",
        hyperlink: "#0563C1",
        followedHyperlink: "#954F72",
    });

    const [pageNum, setPageNum] = useState<number>(1);
    const [days, setDays] = useState<number>(3);
    const [pageSize, setPageSize] = useState<number>(20);
    const [showShimmer, setShowShimmer] = useState<boolean>(false);
    const history = useNavigate();
    const [data, setData] = useState<OnlineWorkflow[]>([]);
    const [isButtonDisabled, setIsButtonDisabled] = useState<boolean>(false);
    const [isFeedbackDialogOpen, setIsFeedbackDialogOpen] = useState(false);
    const [columns, setColumns] = useState<IColumn[]>(initialColumns);

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files) {
            console.log(event.target.files[0]);
            setFile(event.target.files[0]);
            setFileName(event.target.files[0].name);
            setFileUrl(null);
        }
    };

    const handleReverseFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files) {
            console.log(event.target.files[0]);
            setReverseFile(event.target.files[0]);
            setReverseFileName(event.target.files[0].name);
            setReverseFileUrl(null);
        }
    };

    const handleCompressFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files) {
            console.log(event.target.files[0]);
            setCompressFile(event.target.files[0]);
            setCompressFileName(event.target.files[0].name);
            setCompressFileUrl(null);
        }
    };

    const handleTemplateFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files) {
            const file = event.target.files[0];
            if (file.type === "application/vnd.openxmlformats-officedocument.presentationml.presentation") {
                setTemplateFile(file);
                setTemplateFileName(file.name);
                setChangedFileUrl(null);
            } else {
                alert("Please upload a PPTX file");
            }
        }
    };

    const handleChangeImageFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files) {
            const file = event.target.files[0];
            if (file.type === "image/png" || file.type === "image/jpeg") {
                setChangeImageFile(file);
                setChangedFileUrl(null);
            } else {
                alert("Please upload a PNG or JPG image file");
            }
        }
    };

    const handleChangeImageSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setIsButtonDisabled(true);
        try {
            if (templateFile && changeImageFile) {
                const formData = new FormData();
                formData.append("template", templateFile);
                formData.append("image", changeImageFile);
                changeImage({ formData })
                    .then((res) => {
                        const url = window.URL.createObjectURL(new Blob(
                            [res.data],
                            { type: "application/vnd.openxmlformats-officedocument.presentationml.presentation" },
                        ));
                        setChangedFileUrl(url);
                        setIsButtonDisabled(false);
                    })
                    .catch((err) => {
                        if (err.response) {
                            console.log(err.response.data);
                            console.log(err.response.status);
                            console.log(err.response.headers);
                            alert(err.response.data);
                        } else {
                            alert(err);
                        }
                        setIsButtonDisabled(false);
                    });
            } else {
                console.error("Template and image files must not be empty");
                setIsButtonDisabled(false);
            }
        } finally {
            console.log("change image done.");
        }
    };

    const handleSlideImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files) {
            const file = event.target.files[0];
            if (file.type === "image/png" || file.type === "image/jpeg") {
                setSlideImage(file);
                setSlideImageName(file.name);
                setSlideFileUrl(null);
            } else {
                alert("Please upload a PNG or JPG image file");
            }
        }
    };

    const handleSlideImageSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setIsButtonDisabled(true);
        try {
            if (slideImage) {
                const formData = new FormData();
                formData.append("image", slideImage);
                generateOneSlide({ formData })
                    .then((res) => {
                        const url = window.URL.createObjectURL(new Blob(
                            [res.data],
                            { type: "application/vnd.openxmlformats-officedocument.presentationml.presentation" },
                        ));
                        setSlideFileUrl(url);
                        setIsButtonDisabled(false);
                    })
                    .catch((err) => {
                        if (err.response) {
                            console.log(err.response.data);
                            console.log(err.response.status);
                            console.log(err.response.headers);
                            alert(err.response.data);
                        } else {
                            alert(err);
                        }
                        setIsButtonDisabled(false);
                    });
            } else {
                console.error("Image file must not be empty");
                setIsButtonDisabled(false);
            }
        } finally {
            console.log("generate one slide done.");
        }
    };

    const handleImageFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files) {
            // Clean up existing preview URLs
            imagePreviewUrls.forEach((url) => URL.revokeObjectURL(url));

            // Create new preview URLs
            const previewUrls = Array.from(event.target.files).map((file) => URL.createObjectURL(file));

            setImageFiles(event.target.files);
            setImagePreviewUrls(previewUrls);
        }
        setAltTextUrls(null);
    };

    // Cleanup preview URLs when component unmounts
    useEffect(
        () => () => imagePreviewUrls.forEach((url) => URL.revokeObjectURL(url)),
        [imagePreviewUrls],
    );

    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setIsButtonDisabled(true);
        try {
            if (file) {
                const formData = new FormData();
                formData.append("file", file);
                console.log(file);
                postProcessFile({ formData })
                    .then((res) => {
                        const url = window.URL.createObjectURL(new Blob(
                            [res.data],
                            { type: res.headers["content-type"] || "application/octet-stream" },
                        ));
                        setFileUrl(url);
                        setIsButtonDisabled(false);
                    })
                    .catch((err) => {
                        if (err.response) {
                            // The request was made and the server responded with a status code
                            // that falls out of the range of 2xx
                            console.log(err.response.data);
                            console.log(err.response.status);
                            console.log(err.response.headers);
                            alert(err.response.data);
                        } else {
                            alert(err);
                        }
                        setIsButtonDisabled(false);
                    });
            } else {
                console.error("file and path must not be empty");
                setIsButtonDisabled(false);
            }
        } finally {
            console.log("postprocess done.");
        }
    };

    const handleReverseSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setIsButtonDisabled(true);
        try {
            if (reverseFile) {
                const formData = new FormData();
                formData.append("file", reverseFile);
                console.log(file);
                reverseSlidemasterFile({ formData })
                    .then((res) => {
                        const url = window.URL.createObjectURL(new Blob(
                            [res.data],
                            { type: "application/vnd.openxmlformats-officedocument.presentationml.presentation" },
                        ));
                        setReverseFileUrl(url);
                        setIsButtonDisabled(false);
                    })
                    .catch((err) => {
                        if (err.response) {
                            // The request was made and the server responded with a status code
                            // that falls out of the range of 2xx
                            console.log(err.response.data);
                            console.log(err.response.status);
                            console.log(err.response.headers);
                            alert(err.response.data);
                        } else {
                            alert(err);
                        }
                        setIsButtonDisabled(false);
                    });
            } else {
                console.error("file and path must not be empty");
                setIsButtonDisabled(false);
            }
        } finally {
            console.log("reverse slidemaster done.");
        }
    };

    const handleGenerateImageSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setAltTextUrls(null);
        setIsButtonDisabled(true);

        interface RetryResult {
            index: number;
            result: string;
            error?: any;
        }

        const processImages = async (imagesToProcess: File[], indices: number[]): Promise<RetryResult[]> => {
            const promises = imagesToProcess.map((file, i) => {
                const formData = new FormData();
                formData.append("images", file);
                return generateAltTextImages({ formData }).then(
                    (response) => ({
                        index: indices[i],
                        result: response.data[0] || "",
                    } as RetryResult),
                ).catch(
                    (error) => ({
                        index: indices[i],
                        result: "",
                        error,
                    } as RetryResult),
                );
            });
            return Promise.all(promises);
        };

        try {
            if (!imageFiles) {
                console.error("file and path must not be empty");
                return;
            }

            const filesArray = Array.from(imageFiles);
            const results: string[] = new Array(filesArray.length).fill("");

            // Initial attempt with all images
            const initialResults = await processImages(filesArray, filesArray.map((_, i) => i));
            initialResults.forEach((res) => {
                if (res.result) {
                    results[res.index] = res.result;
                }
            });
            setAltTextUrls([...results]);

            // Get all empty indices
            const emptyIndices = results.map((result, index) => (result ? -1 : index))
                .filter((index) => index !== -1);

            if (emptyIndices.length > 0) {
                // Create array of retry attempts
                const retryPromises = Array.from({ length: Math.min(10, emptyIndices.length) })
                    .map((_, attemptIndex) => {
                        const currentIndices = emptyIndices.filter((index) => !results[index]);
                        if (currentIndices.length === 0) return null;

                        console.log(`Retry attempt ${attemptIndex + 1} for ${currentIndices.length} empty results`);
                        const imagesToRetry = currentIndices.map((index) => filesArray[index]);
                        return processImages(imagesToRetry, currentIndices);
                    })
                    .filter(Boolean);

                // Process retries in parallel and handle results
                const allRetryResults = await Promise.all(retryPromises);

                allRetryResults.forEach((retryResults) => {
                    if (!retryResults) return;

                    retryResults.forEach((retry) => {
                        if (retry.error) {
                            console.error(`Retry failed for image ${retry.index}:`, retry.error);
                        } else if (retry.result) {
                            results[retry.index] = retry.result;
                        }
                    });
                    // Update UI after processing each retry batch
                    setAltTextUrls([...results]);
                });
            }
        } catch (err: any) {
            if (err.response) {
                console.log(err.response.data);
                console.log(err.response.status);
                console.log(err.response.headers);
                alert(err.response.data);
            } else {
                alert(err);
            }
        } finally {
            setIsButtonDisabled(false);
            console.log("generate image slidemaster done.");
        }
    };

    const handleCompress = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setCompressFileUrl(null);
        setIsButtonDisabled(true);
        try {
            if (compressFile) {
                const formData = new FormData();
                formData.append("file", compressFile);
                compressTemplate({ formData, quality: imageQuality })
                    .then((res) => {
                        const url = window.URL.createObjectURL(new Blob(
                            [res.data],
                            { type: "application/vnd.openxmlformats-officedocument.presentationml.presentation" },
                        ));

                        setCompressFileUrl(url);
                        setIsButtonDisabled(false);
                    })
                    .catch((err) => {
                        if (err.response) {
                            // The request was made and the server responded with a status code
                            // that falls out of the range of 2xx
                            console.log(err.response.data);
                            console.log(err.response.status);
                            console.log(err.response.headers);
                            alert(err.response.data);
                        } else {
                            alert(err);
                        }
                        setIsButtonDisabled(false);
                    });
            } else {
                console.error("file and path must not be empty");
                setIsButtonDisabled(false);
            }
        } finally {
            console.log("generate image slidemaster done.");
        }
    };

    const onRenderItemColumn = (item: OnlineWorkflow, index?: number, column?: IColumn) => {
        const fieldContent = item[column?.fieldName as keyof OnlineWorkflow];
        switch (column?.key) {
            case "startTimestamp":
                return fieldContent ? new Date(fieldContent as string).toLocaleString("zh-CN", {
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                }) : "";
            case "endTimestamp":
                return fieldContent ? new Date(fieldContent as string).toLocaleString("zh-CN", {
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                }) : "";
            case "runDuration":
                return fieldContent ? `${Number(fieldContent).toFixed(1)}s` : "";
            case "batch":
                return (item.input?.[column?.fieldName as keyof TopicInput] as string);
            case "systemVersion":
                return (item.input?.[column?.fieldName as keyof TopicInput] as string);
            case "productVersion":
                return (item.input?.[column?.fieldName as keyof TopicInput] as string);
            case "style":
                return (item.input?.[column?.fieldName as keyof TopicInput] as string);
            case "version":
                return fieldContent as string;
            case "reviewScores": {
                // 如果字段为空或者是空字符串,显示 "-"
                if (!fieldContent || fieldContent === "") return "-";

                const fieldStr = fieldContent as string;

                // 尝试先解析 JSON 格式
                try {
                    const scores = JSON.parse(fieldStr);
                    if (typeof scores === "object" && scores !== null) {
                        // 如果解析后是空对象,也显示 "-"
                        if (Object.keys(scores).length === 0) return "-";

                        // 定义缩写映射和固定顺序
                        const scoreLabels: { [key: string]: string } = {
                            Fidelity: "Fid",
                            Aesthetics: "Aes",
                            Consistency: "Con",
                            Narrative: "Nar",
                        };

                        // 按固定顺序显示
                        const orderedKeys = ["Fidelity", "Aesthetics", "Consistency", "Narrative"];
                        const displayText = orderedKeys
                            .filter((key) => scores[key] !== undefined && scores[key] !== null)
                            .map((key) => `${scoreLabels[key]}:${scores[key]}`)
                            .join(" | ");

                        return (
                            <div title={JSON.stringify(scores, null, 2)}>
                                {displayText || "-"}
                            </div>
                        );
                    }
                } catch (error) {
                    // 不是 JSON 格式,继续处理字符串格式
                }

                // 处理字符串格式: "55 | 30 | 74 | 50"
                // 格式为: Fidelity | Aesthetics | Consistency | Narrative
                if (fieldStr.includes("|")) {
                    const values = fieldStr.split("|").map((v) => v.trim());
                    const labels = ["Fid", "Aes", "Con", "Nar"];

                    if (values.length === 4) {
                        const displayText = values
                            .map((value, index) => `${labels[index]}:${value}`)
                            .join(" | ");

                        return (
                            <div title={`Fidelity: ${values[0]}, Aesthetics: ${values[1]}, Consistency: ${values[2]}, Narrative: ${values[3]}`}>
                                {displayText}
                            </div>
                        );
                    }
                }

                // 如果格式不匹配,直接显示原始文本
                return (
                    <div title={fieldStr}>
                        {fieldStr}
                    </div>
                );
            }
            case "toggleSettings": {
                if (!fieldContent) return "";
                try {
                    const settings = JSON.parse(fieldContent as string);
                    const displayText = Object.entries(settings)
                        .map(([key, value]) => `${key}: ${value}`)
                        .join(", ");
                    return (
                        <div title={JSON.stringify(settings, null, 2)}>
                            {displayText}
                        </div>
                    );
                } catch (error) {
                    return (
                        <div title={fieldContent as string}>
                            {fieldContent as string}
                        </div>
                    );
                }
            }
            case "renderContent":
                return (item.input?.[column?.fieldName as keyof TopicInput]?.toString());
            case "outlineGenerationOptions":
                return JSON.stringify(item.input?.[column?.fieldName as keyof TopicInput]);
            case "themeGenerationOptions":
                return JSON.stringify(item.input?.[column?.fieldName as keyof TopicInput]);
            case "topic": {
                const topicValue = item.input?.[column?.fieldName as keyof TopicInput] as string;
                return (
                    <div title={String(topicValue || "")}>
                        {topicValue}
                    </div>
                );
            }
            case "industry":
                return (item.input?.[column?.fieldName as keyof TopicInput] as string);
            case "purpose":
                return (item.input?.[column?.fieldName as keyof TopicInput] as string);
            case "handle":
                return (
                    <div className={style.btnBox}>
                        <a
                            href={`/session/detail?sessionId=${item.sessionId}`}
                            style={{ textDecoration: "none", display: "inline-block" }}
                        >
                            <PrimaryButton
                                iconProps={{ iconName: "PageList" }}
                                title="Session详情"
                                ariaLabel="Session详情"
                                styles={{
                                    root: { minWidth: 32, padding: "0 8px", height: 32 },
                                    icon: { margin: 0 },
                                }}
                            />
                        </a>
                        <a
                            href={`/workflow/detail?workflowId=${item.workflowId}`}
                            style={{ textDecoration: "none", display: "inline-block" }}
                        >
                            <PrimaryButton
                                iconProps={{ iconName: "Documentation" }}
                                title="Workflow详情"
                                ariaLabel="Workflow详情"
                                styles={{
                                    root: { minWidth: 32, padding: "0 8px", height: 32 },
                                    icon: { margin: 0 },
                                }}
                            />
                        </a>
                        <PrimaryButton
                            iconProps={{ iconName: "Delete" }}
                            title="删除"
                            ariaLabel="删除"
                            styles={{
                                root: { minWidth: 32, padding: "0 8px", height: 32 },
                                icon: { margin: 0 },
                            }}
                            onClick={async () => {
                                await handleDelete(`${item.workflowId}`);
                                history("/workflow");
                            }}
                        />
                    </div>
                );
            default:
                return fieldContent as React.ReactNode;
        }
    };

    const onColumnResize = (column?: IColumn, newWidth?: number, columnIndex?: number) => {
        if (column && newWidth !== undefined && columnIndex !== undefined) {
            const newColumns = [...columns];
            newColumns[columnIndex] = { ...newColumns[columnIndex], currentWidth: newWidth };
            setColumns(newColumns);
        }
    };

    const [workflowId, setWorkflowId] = useState<string>("");
    const [sessionId, setSessionId] = useState<string>("");
    const [workflowTemplateName, setWorkflowTemplateName] = useState<string>("OnlineClippy");
    const [options, setOptions] = useState<IDropdownOption[]>([]);

    useEffect(() => {
        queryWorkflowTemplates()
            .then((res) => {
                if (res) {
                    setOptions(res.map((item: OnlineWorkflowTemplates) => ({
                        key: item.name,
                        text: item.name,
                    })));
                }
            })
            .catch();
    }, []);

    useEffect(() => {
        setData([]);
        setShowShimmer(true);
        const queryData = () => queryWorkflowList({
            days, start: (pageNum - 1) * pageSize, size: pageSize, workflowId, sessionId, workflowTemplateName,
        }).then((res) => {
            if (Array.isArray(res)) {
                setData(res);
            }
        }).catch((err) => {
            // alert(err);
            if (err.response && err.response.status === 401) {
                console.log(err);
            } else {
                alert(err);
            }
        }).finally(() => {
            setShowShimmer(false);
        });
        queryData();
        const interval = setInterval(() => queryData(), 20000);
        return () => clearInterval(interval);
    }, [pageNum, days, pageSize, workflowId, sessionId, workflowTemplateName]);

    const handleThemeColorFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files) {
            const file = event.target.files[0];
            if (file.type === "application/vnd.openxmlformats-officedocument.presentationml.presentation") {
                setThemeColorFile(file);
                setThemeColorFileName(file.name);
                setThemeColorFileUrl(null);
            } else {
                alert("Please upload a PPTX file");
            }
        }
    };

    const handleColorChange = (key: string, value: string) => {
        setThemeColors((prev) => ({ ...prev, [key]: value }));
    };

    const handleThemeColorSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setIsButtonDisabled(true);
        try {
            if (themeColorFile) {
                const formData = new FormData();
                formData.append("file", themeColorFile);
                formData.append("themeColorsJson", JSON.stringify(themeColors));
                changeThemeColor({ formData })
                    .then((res) => {
                        const url = window.URL.createObjectURL(new Blob(
                            [res.data],
                            { type: "application/vnd.openxmlformats-officedocument.presentationml.presentation" },
                        ));
                        setThemeColorFileUrl(url);
                        setIsButtonDisabled(false);
                    })
                    .catch((err) => {
                        if (err.response) {
                            console.log(err.response.data);
                            console.log(err.response.status);
                            console.log(err.response.headers);
                            alert(err.response.data);
                        } else {
                            alert(err);
                        }
                        setIsButtonDisabled(false);
                    });
            } else {
                console.error("Template file must not be empty");
                setIsButtonDisabled(false);
            }
        } finally {
            console.log("change theme color done.");
        }
    };

    return (
        <div className={style.workflow}>
            <h1>Workflows</h1>
            <Stack tokens={{ childrenGap: 20 }}>
                <div className={style.toolSection}>
                    <Stack
                        horizontal
                        tokens={{
                            childrenGap: 20,
                        }}
                        horizontalAlign="start"
                        className={style.newWorkflow}
                        wrap
                    >
                        <div>
                            <SpinButton
                                labelPosition={Position.top}
                                className={style.input}
                                value={String(days)}
                                min={0}
                                label="Days"
                                onChange={(e, value) => setDays(Number(value))}
                            />
                        </div>
                        <div>
                            <TextField
                                label="SessionId"
                                className={style.input}
                                value={sessionId}
                                onChange={(e, value) => {
                                    setSessionId(value || "");
                                }}
                            />
                        </div>
                        <div>
                            <TextField
                                label="WorkflowId"
                                className={style.input}
                                value={workflowId}
                                onChange={(e, value) => {
                                    if (value?.length === 36) {
                                        setWorkflowId(value || "");
                                    } else {
                                        setWorkflowId("");
                                    }
                                }}
                            />
                        </div>
                        <div>
                            <Dropdown
                                required
                                label="workflowTemplateName"
                                onChange={(e, item) => {
                                    setWorkflowTemplateName(item ? String(item.key) : "OnlineModernV2");
                                }}
                                options={options}
                            />
                        </div>
                    </Stack>
                </div>
                <div style={{ position: "relative", height: "calc(100vh - 320px)" }}>
                    <ScrollablePane>
                        <ShimmeredDetailsList
                            className={style.detailListSticky}
                            items={data}
                            columns={columns}
                            setKey="workflowId"
                            layoutMode={DetailsListLayoutMode.fixedColumns}
                            constrainMode={ConstrainMode.unconstrained}
                            onRenderItemColumn={onRenderItemColumn}
                            onColumnResize={onColumnResize}
                            isHeaderVisible
                            selectionMode={0}
                            enableShimmer={showShimmer}
                        />
                    </ScrollablePane>
                </div>
                <div className={style.pag}>
                    <Dropdown
                        options={pageSizeOptions}
                        selectedKey={pageSize}
                        style={{ width: 200, marginRight: 20 }}
                        onChange={(e, item) => setPageSize(Number(item?.key))}
                    />
                    <button
                        type="button"
                        className={style.nextPage}
                        onClick={() => setPageNum(pageNum > 1 ? pageNum - 1 : 1)}
                    >
                        &lt;
                    </button>
                    <span className={style.nextPage}>
                        {pageNum}
                    </span>
                    <button
                        type="button"
                        className={style.nextPage}
                        onClick={() => setPageNum(pageNum + 1)}
                    >
                        &gt;
                    </button>
                </div>
                <Stack horizontal tokens={{ childrenGap: 12 }} styles={{ root: { marginBottom: 24 } }}>
                    <PrimaryButton
                        text="新建任务"
                        iconProps={{ iconName: "Add" }}
                        title="Add"
                        ariaLabel="Add"
                        onClick={() => {
                            history("/workflow/add");
                        }}
                    />
                    <DefaultButton
                        text="Clippy"
                        iconProps={{ iconName: "Robot" }}
                        onClick={() => {
                            history("/clippy");
                        }}
                    />
                    <DefaultButton
                        text="Image"
                        iconProps={{ iconName: "Picture" }}
                        onClick={() => {
                            history("/image");
                        }}
                    />
                    <DefaultButton
                        text="Icon"
                        iconProps={{ iconName: "Emoji2" }}
                        onClick={() => {
                            history("/icon");
                        }}
                    />
                    <DefaultButton
                        text="Icon Create"
                        iconProps={{ iconName: "Add" }}
                        onClick={() => {
                            history("/icon-create");
                        }}
                    />
                    <DefaultButton
                        text="Icon Create with Style"
                        iconProps={{ iconName: "PictureCenter" }}
                        onClick={() => {
                            history("/icon-create-with-style");
                        }}
                    />
                    <DefaultButton
                        text="Feedback"
                        iconProps={{ iconName: "Feedback" }}
                        onClick={() => setIsFeedbackDialogOpen(true)}
                    />
                </Stack>
                <FeedbackDialog isOpen={isFeedbackDialogOpen} onDismiss={() => setIsFeedbackDialogOpen(false)} />
                <div className={style.toolSection}>
                    <Stack.Item>
                        <form onSubmit={handleSubmit}>
                            <Stack horizontal tokens={{ childrenGap: 20 }} styles={{ root: { alignItems: "flex-end" } }}>
                                <Stack.Item styles={{ root: { minWidth: 180, fontWeight: 600 } }}>Postprocess</Stack.Item>
                                <Stack.Item>
                                    <input type="file" onChange={handleFileChange} />
                                </Stack.Item>
                                <Stack.Item>
                                    <PrimaryButton
                                        type="submit"
                                        disabled={isButtonDisabled || !file}
                                        text="Postprocess"
                                    />
                                </Stack.Item>
                            </Stack>
                            {fileUrl && (
                                <Stack.Item styles={{ root: { marginTop: 10 } }}>
                                    <a href={fileUrl} download={fileName}>
                                        Download post-processed file
                                    </a>
                                </Stack.Item>
                            )}
                        </form>
                    </Stack.Item>
                </div>
                <div className={style.toolSection}>
                    <Stack.Item>
                        <form onSubmit={handleReverseSubmit}>
                            <Stack horizontal tokens={{ childrenGap: 20 }} styles={{ root: { alignItems: "flex-end" } }}>
                                <Stack.Item styles={{ root: { minWidth: 180, fontWeight: 600 } }}>Reverse SlideMaster</Stack.Item>
                                <Stack.Item>
                                    <input type="file" onChange={handleReverseFileChange} />
                                </Stack.Item>
                                <Stack.Item>
                                    <PrimaryButton
                                        type="submit"
                                        disabled={isButtonDisabled || !reverseFile}
                                        text="Reverse SlideMaster"
                                    />
                                </Stack.Item>
                            </Stack>
                            {reverseFileUrl && (
                                <Stack.Item styles={{ root: { marginTop: 10 } }}>
                                    <a href={reverseFileUrl} download={reverseFileName}>
                                        Download reverse-slidemaster file
                                    </a>
                                </Stack.Item>
                            )}
                        </form>
                    </Stack.Item>
                </div>
                <div className={style.toolSection}>
                    <Stack.Item>
                        <form onSubmit={handleGenerateImageSubmit}>
                            <Stack horizontal tokens={{ childrenGap: 20 }} styles={{ root: { alignItems: "flex-end" } }}>
                                <Stack.Item styles={{ root: { minWidth: 180, fontWeight: 600 } }}>Generate and AltText</Stack.Item>
                                <Stack.Item>
                                    <input type="file" multiple onChange={handleImageFileChange} />
                                </Stack.Item>
                                <Stack.Item>
                                    <PrimaryButton
                                        type="submit"
                                        disabled={isButtonDisabled || !imageFiles || imageFiles.length === 0}
                                        text="Generate and AltText"
                                    />
                                </Stack.Item>
                            </Stack>
                            {altTextUrls && imageFiles && (
                                <Stack.Item styles={{ root: { marginTop: 20 } }}>
                                    <div className={style.imageResults}>
                                        {altTextUrls.map((result, index) => (
                                            <div key={result} className={style.imageResultCard}>
                                                <div className={style.filename}>{imageFiles[index].name}</div>
                                                <img
                                                    src={imagePreviewUrls[index]}
                                                    alt={`Preview of ${imageFiles[index].name}`}
                                                    className={style.imagePreview}
                                                />
                                                <div className={style.resultLabel}>Generated Description:</div>
                                                <p className={style.resultText}>{result}</p>
                                            </div>
                                        ))}
                                    </div>
                                </Stack.Item>
                            )}
                        </form>
                    </Stack.Item>
                </div>
                <div className={style.toolSection}>
                    <Stack.Item>
                        <form onSubmit={handleSlideImageSubmit}>
                            <Stack horizontal tokens={{ childrenGap: 20 }} styles={{ root: { alignItems: "flex-end" } }}>
                                <Stack.Item styles={{ root: { minWidth: 180, fontWeight: 600 } }}>Generate OneSlide Template v1.0</Stack.Item>
                                <Stack.Item>
                                    <input
                                        type="file"
                                        onChange={handleSlideImageChange}
                                        accept="image/png,image/jpeg"
                                    />
                                </Stack.Item>
                                <Stack.Item>
                                    <PrimaryButton
                                        type="submit"
                                        disabled={isButtonDisabled || !slideImage}
                                        text="Generate OneSlide Template v1.0"
                                    />
                                </Stack.Item>
                            </Stack>
                            {slideFileUrl && (
                                <Stack.Item styles={{ root: { marginTop: 10 } }}>
                                    <a href={slideFileUrl} download={slideImageName?.replace(/\.(png|jpg)$/i, ".pptx")}>
                                        Download generated slide
                                    </a>
                                </Stack.Item>
                            )}
                        </form>
                    </Stack.Item>
                </div>
                <div className={style.toolSection}>
                    <Stack.Item>
                        <form onSubmit={handleChangeImageSubmit}>
                            <Stack horizontal tokens={{ childrenGap: 20 }} styles={{ root: { alignItems: "flex-end" } }}>
                                <Stack.Item styles={{ root: { minWidth: 180, fontWeight: 600 } }}>Change OneSlide Template Image</Stack.Item>
                                <Stack.Item>
                                    <div>
                                        <div>OneSlide Template:</div>
                                        <input
                                            type="file"
                                            onChange={handleTemplateFileChange}
                                            accept=".pptx"
                                        />
                                    </div>
                                </Stack.Item>
                                <Stack.Item>
                                    <div>
                                        <div>New Image:</div>
                                        <input
                                            type="file"
                                            onChange={handleChangeImageFileChange}
                                            accept="image/png,image/jpeg"
                                        />
                                    </div>
                                </Stack.Item>
                                <Stack.Item>
                                    <PrimaryButton
                                        type="submit"
                                        disabled={isButtonDisabled || !templateFile || !changeImageFile}
                                        text="Change OneSlide Template Image"
                                    />
                                </Stack.Item>
                            </Stack>
                            {changedFileUrl && (
                                <Stack.Item styles={{ root: { marginTop: 10 } }}>
                                    <a href={changedFileUrl} download={templateFileName}>
                                        Download modified presentation
                                    </a>
                                </Stack.Item>
                            )}
                        </form>
                    </Stack.Item>
                </div>
                <div className={style.toolSection}>
                    <Stack.Item>
                        <form onSubmit={handleThemeColorSubmit}>
                            <Stack horizontal tokens={{ childrenGap: 20 }} styles={{ root: { alignItems: "flex-end" } }}>
                                <Stack.Item styles={{ root: { minWidth: 180, fontWeight: 600 } }}>Change Theme Color</Stack.Item>
                                <Stack.Item>
                                    <div>
                                        <div>Template File:</div>
                                        <input
                                            type="file"
                                            onChange={handleThemeColorFileChange}
                                            accept=".pptx"
                                        />
                                    </div>
                                </Stack.Item>
                                <Stack.Item>
                                    <DefaultButton
                                        text="Select Colors"
                                        onClick={() => setIsColorPickerDialogOpen(true)}
                                    />
                                </Stack.Item>
                                <Stack.Item>
                                    <PrimaryButton
                                        type="submit"
                                        disabled={isButtonDisabled || !themeColorFile}
                                        text="Change Theme Color"
                                    />
                                </Stack.Item>
                            </Stack>
                            {themeColorFileUrl && (
                                <Stack.Item styles={{ root: { marginTop: 10 } }}>
                                    <a href={themeColorFileUrl} download={themeColorFileName}>
                                        Download modified presentation
                                    </a>
                                </Stack.Item>
                            )}
                        </form>
                        <Dialog
                            hidden={!isColorPickerDialogOpen}
                            onDismiss={() => setIsColorPickerDialogOpen(false)}
                            dialogContentProps={{
                                type: DialogType.largeHeader,
                                title: "Select Theme Colors",
                            }}
                            modalProps={{
                                isBlocking: false,
                                styles: { main: { maxWidth: "600px" } },
                            }}
                        >
                            <div className={style.colorGrid}>
                                {Object.keys(themeColors).map((key) => (
                                    <div key={key} className={style.colorPickerContainer}>
                                        <label htmlFor={`${key}-color-text`}>{key}</label>
                                        <input
                                            type="color"
                                            value={themeColors[key]}
                                            onChange={(e) => handleColorChange(key, e.target.value)}
                                            aria-label={`${key} color picker`}
                                        />
                                        <input
                                            id={`${key}-color-text`}
                                            type="text"
                                            value={themeColors[key]}
                                            onChange={(e) => handleColorChange(key, e.target.value)}
                                        />
                                    </div>
                                ))}
                            </div>
                            <DialogFooter>
                                <PrimaryButton onClick={() => setIsColorPickerDialogOpen(false)} text="Done" />
                            </DialogFooter>
                        </Dialog>
                    </Stack.Item>
                </div>
                <div className={style.toolSection}>
                    <Stack.Item>
                        <form onSubmit={handleCompress}>
                            <Stack horizontal tokens={{ childrenGap: 20 }} styles={{ root: { alignItems: "flex-end" } }}>
                                <Stack.Item styles={{ root: { minWidth: 180, fontWeight: 600 } }}>Compress Image Quality</Stack.Item>
                                <Stack.Item>
                                    <SpinButton
                                        label="Image Quality (1-100)"
                                        min={1}
                                        max={100}
                                        value={String(imageQuality)}
                                        onChange={(_, newValue) => {
                                            const value = Number(newValue);
                                            if (!Number.isNaN(value) && value >= 1 && value <= 100) {
                                                setImageQuality(value);
                                            }
                                        }}
                                        onValidate={(value) => {
                                            const numValue = Math.floor(Number(value));
                                            if (Number.isNaN(numValue)) return "1";
                                            if (numValue < 1) return "1";
                                            if (numValue > 100) return "100";
                                            return String(numValue);
                                        }}
                                        styles={{ root: { width: 200 } }}
                                    />
                                </Stack.Item>
                                <Stack.Item>
                                    <input type="file" onChange={handleCompressFileChange} />
                                </Stack.Item>
                                <Stack.Item>
                                    <PrimaryButton
                                        type="submit"
                                        disabled={isButtonDisabled || !compressFile || imageQuality < 1 || imageQuality > 100}
                                        text="Compress Image Quality"
                                    />
                                </Stack.Item>
                            </Stack>
                            {compressFileUrl && (
                                <Stack.Item>
                                    <a href={compressFileUrl} download={compressFileName}>
                                        Download Compressed File
                                    </a>
                                </Stack.Item>
                            )}
                        </form>
                    </Stack.Item>
                </div>
            </Stack>
        </div>
    );
};
